// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo } from 'react';

import { type PartCategoryId, type PartAttributeDefinitionVariantId, useEvolvePaginatedGet } from '@evolvemep/api-ts';
import { Text, Flex, Stack, Button, Checkbox } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import type { WithOptional, UseState } from 'helpers/helper-types';
import { isNil } from 'helpers/isNotNil';
import { TriggerOnVisible } from 'hooks/useOnScreen';

type Props = {
  selectedCategoryId: PartCategoryId;
  partFilter: PartAttributeDefinitionVariantId[];
  onPartFilterChanged: UseState<PartAttributeDefinitionVariantId[]>;
};

const CategoryFiltersWrapped = ({ partFilter, onPartFilterChanged, selectedCategoryId }: Props) => {
  const defaultOpts = useMemo(
    () => ({
      defaultConfig: {
        params: {
          partCategoryId: selectedCategoryId,
        },
      },
    }),
    [selectedCategoryId],
  );
  const {
    data: categoryAttributes,
    setDefaultOpts,
    fetchNextPage,
    loading,
    reset,
  } = useEvolvePaginatedGet('shop/partCatalog/globalAttribute', defaultOpts);
  useEffect(() => {
    setDefaultOpts(defaultOpts);
    // TODO: Is this needed?
    reset();
  }, [defaultOpts, reset, setDefaultOpts]);

  return (
    <Stack flex={1} gap="xs" style={{ overflowY: 'scroll' }}>
      {categoryAttributes.length === 0 && !loading && (
        <Text fz="sm" c="dimmed">
          No filters available.
        </Text>
      )}
      {categoryAttributes.map((attr) => (
        <Stack key={attr.partAttributeDefinitionId} gap={6}>
          <Text
            fw={attr.variants.some((v) => partFilter.includes(v.partAttributeDefinitionVariantId)) ? 'bold' : undefined}
            style={{ userSelect: 'none' }}
          >
            {attr.name}
          </Text>
          {attr.variants
            .toSorted((a, b) => a.name.localeCompare(b.name))
            .map((v) => (
              <Checkbox
                key={v.partAttributeDefinitionVariantId}
                ml="xs"
                size="sm"
                label={v.name}
                checked={partFilter.includes(v.partAttributeDefinitionVariantId)}
                onChange={(e) => {
                  if (e.currentTarget.checked) {
                    onPartFilterChanged([...partFilter, v.partAttributeDefinitionVariantId]);
                  } else {
                    onPartFilterChanged(partFilter.filter((f) => f !== v.partAttributeDefinitionVariantId));
                  }
                }}
              />
            ))}
        </Stack>
      ))}
      <TriggerOnVisible onVisible={fetchNextPage} loading={loading} />
    </Stack>
  );
};

export const CategoryFilters = ({ selectedCategoryId, ...props }: WithOptional<Props, 'selectedCategoryId'>) => (
  <Stack style={{ overflow: 'hidden' }} gap="xs" flex={1}>
    <Flex justify="space-between">
      <Text fw="bold">Attribute Filters</Text>
      {props.partFilter.length > 0 && (
        <AgGridStyleTooltip label="Clear filters" withArrow>
          <Button size="compact-xs" variant="light" onClick={() => props.onPartFilterChanged([])}>
            <EvolveIcon icon="ClearFilters" size="sm" />
          </Button>
        </AgGridStyleTooltip>
      )}
    </Flex>
    {isNil(selectedCategoryId) ? (
      <Text fz="sm" c="dimmed">
        Select a category to view filters.
      </Text>
    ) : (
      <CategoryFiltersWrapped selectedCategoryId={selectedCategoryId} {...props} />
    )}
  </Stack>
);
